import React, { useEffect, useState } from "react";
import styles from "./FumigationCompany.module.scss";
import { ATMGrid } from "../../../components/atoms/ATMGrid/ATMGrid.component";
import { ATMSegment } from "../../../components/atoms/ATMSegment/ATMSegment.component";
import { ExpandMore, ExpandLess, CorporateFare } from "@mui/icons-material";
import { ATMHeader } from "../../../components/atoms/ATMHeader/ATMHeader.component";
import FumigationCompanyConstants from "constants/FumigationCompanyConstants/FumigationCompanyConstants";
import { applicationData } from "contexts/applicationDataFieldTypes";
import useFetchFumigationCompanies from "hooks/useFetchFumigationCompanies";
import { HeaderFieldKey } from "../validation/fumigationValidation";

interface FumigationCompanyProps {
    applicationData: applicationData;
    setApplicationData: React.Dispatch<React.SetStateAction<applicationData>>;
    toggle: boolean;
    setToggle: (value: boolean) => void;
    control: any;
    fieldErrors?: Partial<Record<HeaderFieldKey, string>>;
    sectionRef?: React.RefObject<HTMLDivElement>;
}

// ── Shared inline styles ──────────────────────────────────────────────────────

const FONT: React.CSSProperties = {
    fontSize: '13px',
    fontFamily: 'Arial, Helvetica, sans-serif',
};

const LABEL_TD: React.CSSProperties = {
    ...FONT,
    color: 'blue',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    paddingRight: '5px',
    verticalAlign: 'middle',
};

const REQUIRED_TD: React.CSSProperties = {
    ...FONT,
    color: 'green',
    paddingRight: '5px',
    verticalAlign: 'middle',
};

const INPUT_TD: React.CSSProperties = {
    ...FONT,
    verticalAlign: 'middle',
    paddingBottom: '5px',
};

const INPUT_STYLE: React.CSSProperties = {
    ...FONT,
    border: '1px solid #999',
    padding: '4px 8px',
    width: '400px',
    maxWidth: '100%',
    height: '30px',
    borderRadius: '3px',
    boxSizing: 'border-box',
};

const SELECT_STYLE: React.CSSProperties = {
    ...FONT,
    border: '1px solid #999',
    padding: '4px 8px',
    width: '400px',
    maxWidth: '100%',
    height: '30px',
    borderRadius: '3px',
    boxSizing: 'border-box',
};

const PHONE_INPUT: React.CSSProperties = {
    ...FONT,
    border: '1px solid #999',
    padding: '2px 3px',
    boxSizing: 'border-box',
};

/** Red outline applied to inputs that failed validation. */
const ERROR_OUTLINE: React.CSSProperties = {
    border: '1px solid #A11003',
    backgroundColor: '#FDF3F2',
};

const ERROR_TEXT: React.CSSProperties = {
    ...FONT,
    color: '#A11003',
    display: 'block',
    marginTop: '2px',
};

// ── Component ─────────────────────────────────────────────────────────────────

const FumigationCompany: React.FC<FumigationCompanyProps> = ({
    applicationData,
    setApplicationData,
    toggle,
    setToggle,
    fieldErrors = {},
    sectionRef,
}) => {
    const [allCompanies, setAllCompanies] = useState<string[]>([]);
    const { data, isLoading, isError } = useFetchFumigationCompanies();

    // Refs for auto-advancing phone inputs
    const phoneAreaRef = React.useRef<HTMLInputElement>(null);
    const phoneMid3Ref = React.useRef<HTMLInputElement>(null);
    const phoneLast4Ref = React.useRef<HTMLInputElement>(null);
    const altPhoneAreaRef = React.useRef<HTMLInputElement>(null);
    const altPhoneMid3Ref = React.useRef<HTMLInputElement>(null);
    const altPhoneLast4Ref = React.useRef<HTMLInputElement>(null);

    /** Merges the error outline into an input style when the field has an error. */
    const withError = (base: React.CSSProperties, field: HeaderFieldKey): React.CSSProperties =>
        fieldErrors[field] ? { ...base, ...ERROR_OUTLINE } : base;

    const errorFor = (field: HeaderFieldKey) => fieldErrors[field] && (
        <span style={ERROR_TEXT} role="alert">{fieldErrors[field]}</span>
    );

    useEffect(() => {
        if (data?.data) {
            setAllCompanies(Array.from(new Set(data.data as string[])));
        }
    }, [data]);

    const handleToggle = () => setToggle(!toggle);

    const handleChange = (field: keyof applicationData, value: string) => {
        setApplicationData(prev => ({ ...prev, [field]: value }));
    };

    const handleCompanySelect = (value: string) => {
        setApplicationData(prev => ({
            ...prev,
            fumigationCompanyName: value,
        }));
    };

    const get = (field: keyof applicationData): string =>
        String(applicationData[field] ?? '');

    /**
     * Auto-advance phone number fields when max length is reached.
     * Only auto-advances on numeric input and when field is full.
     */
    const handlePhoneInput = (
        field: keyof applicationData,
        value: string,
        maxLength: number,
        nextRef: React.RefObject<HTMLInputElement> | null
    ) => {
        // Only allow numeric input
        const numericValue = value.replace(/\D/g, '').slice(0, maxLength);
        handleChange(field, numericValue);

        // Auto-advance to next field when max length is reached
        if (numericValue.length === maxLength && nextRef?.current) {
            nextRef.current.focus();
        }
    };

    return (
        <div ref={sectionRef}>
            <ATMSegment size="tiny" className={styles.commonMargin}>
                <ATMGrid>

                {/* Section header / toggle */}
                <ATMGrid.Row>
                    <ATMGrid.Column width={8} className={styles.segmentsStyle} onClick={handleToggle}>
                        <ATMHeader as="h2" className={styles.headerStyle}>
                            <CorporateFare />
                            {FumigationCompanyConstants.SECTION_TITLE}
                        </ATMHeader>
                    </ATMGrid.Column>
                    <ATMGrid.Column width={8} floated="right" textAlign="right" onClick={handleToggle}>
                        {toggle ? <ExpandLess /> : <ExpandMore />}
                    </ATMGrid.Column>
                </ATMGrid.Row>

                {toggle && (
                    <ATMGrid.Row>
                        <ATMGrid.Column width={16}>

                            {/* Section-level validation errors */}
                            {Object.keys(fieldErrors).length > 0 && (
                                <div role="alert" style={{
                                    ...FONT,
                                    border: '1px solid #A11003',
                                    borderLeft: '4px solid #A11003',
                                    borderRadius: '4px',
                                    background: '#FDF3F2',
                                    padding: '10px 14px',
                                    marginBottom: '10px',
                                }}>
                                    <strong style={{ color: '#A11003', display: 'block', marginBottom: '4px' }}>
                                        Please correct the following:
                                    </strong>
                                    <ul style={{ margin: 0, paddingLeft: '18px', color: '#A11003' }}>
                                        {(Object.values(fieldErrors) as string[])
                                            .filter((v, i, arr) => arr.indexOf(v) === i)
                                            .map((msg, i) => (
                                                <li key={i}>{msg}</li>
                                            ))}
                                    </ul>
                                </div>
                            )}

                            {/* Notice text */}
                            <table style={{ borderCollapse: 'collapse', marginBottom: '6px', width: '100%' }}>
                                <tbody>
                                    <tr><td style={{ ...FONT, color: 'blue' }}>{FumigationCompanyConstants.SECTION_SUBTITLE}</td></tr>
                                    <tr><td style={{ ...FONT, color: 'red', fontWeight: 'bold' }}>{FumigationCompanyConstants.SECTION_SUBTITLE_2}</td></tr>
                                </tbody>
                            </table>

                            <table style={{ borderCollapse: 'collapse', marginBottom: '8px' }}>
                                <tbody>
                                    <tr><td style={{ ...FONT, color: 'green' }}>* Indicates required field</td></tr>
                                </tbody>
                            </table>

                            {/* ── Main fields ─────────────────────────────── */}
                            <table className={styles.formTable}>
                                <tbody>

                                    {/* Fumigation Company List → sets fumigationCompanyName */}
                                    <tr>
                                        <td style={LABEL_TD}>Fumigation Company List</td>
                                        <td style={REQUIRED_TD}>*</td>
                                        <td style={INPUT_TD}>
                                            <select
                                                style={SELECT_STYLE}
                                                value={get('fumigationCompanyName')}
                                                onChange={e => handleCompanySelect(e.target.value)}
                                                aria-label="Fumigation Company List"
                                                disabled={isLoading}
                                            >
                                                <option value="">
                                                    {isLoading ? 'Loading companies...' : 
                                                     isError ? 'Error loading companies' : 
                                                     'Select From List'}
                                                </option>
                                                {allCompanies.map(c => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>

                                    {/* Fumigation Company Name */}
                                    <tr>
                                        <td style={LABEL_TD}>Fumigation Company Name</td>
                                        <td style={REQUIRED_TD}>*</td>
                                        <td style={INPUT_TD}>
                                            <input
                                                type="text"
                                                maxLength={100}
                                                style={withError(INPUT_STYLE, 'fumigationCompanyName')}
                                                value={get('fumigationCompanyName')}
                                                onChange={e => handleChange('fumigationCompanyName', e.target.value)}
                                                autoComplete="off"
                                                aria-label="Fumigation Company Name"
                                                aria-invalid={Boolean(fieldErrors.fumigationCompanyName)}
                                            />
                                            {errorFor('fumigationCompanyName')}
                                        </td>
                                    </tr>

                                    {/* Contact Person */}
                                    <tr>
                                        <td style={LABEL_TD}>Contact Person</td>
                                        <td style={REQUIRED_TD}>*</td>
                                        <td style={INPUT_TD}>
                                            <input
                                                type="text"
                                                maxLength={100}
                                                style={withError(INPUT_STYLE, 'contactPerson')}
                                                value={get('contactPerson')}
                                                onChange={e => handleChange('contactPerson', e.target.value)}
                                                autoComplete="off"
                                                aria-label="Contact Person"
                                                aria-invalid={Boolean(fieldErrors.contactPerson)}
                                            />
                                            {errorFor('contactPerson')}
                                        </td>
                                    </tr>

                                    {/* Telephone Number: Area Code + Mid 3 + Last 4 + Extension */}
                                    <tr>
                                        <td style={LABEL_TD}>Telephone Number</td>
                                        <td style={REQUIRED_TD}>*</td>
                                        <td style={INPUT_TD}>
                                            <input
                                                ref={phoneAreaRef}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={3}
                                                style={withError({ ...PHONE_INPUT, width: '35px' }, 'telephoneNumber')}
                                                value={get('telephoneAreaCode')}
                                                onChange={e => handlePhoneInput('telephoneAreaCode', e.target.value, 3, phoneMid3Ref)}
                                                autoComplete="off"
                                                aria-label="Area Code"
                                                aria-invalid={Boolean(fieldErrors.telephoneNumber)}
                                            />
                                            &nbsp;&nbsp;
                                            <input
                                                ref={phoneMid3Ref}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={3}
                                                style={withError({ ...PHONE_INPUT, width: '35px' }, 'telephoneNumber')}
                                                value={get('telephoneMid3')}
                                                onChange={e => handlePhoneInput('telephoneMid3', e.target.value, 3, phoneLast4Ref)}
                                                autoComplete="off"
                                                aria-label="Phone Mid 3"
                                                aria-invalid={Boolean(fieldErrors.telephoneNumber)}
                                            />
                                            &nbsp;&nbsp;
                                            <input
                                                ref={phoneLast4Ref}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={4}
                                                style={withError({ ...PHONE_INPUT, width: '45px' }, 'telephoneNumber')}
                                                value={get('telephoneLast4')}
                                                onChange={e => handlePhoneInput('telephoneLast4', e.target.value, 4, null)}
                                                autoComplete="off"
                                                aria-label="Phone Last 4"
                                                aria-invalid={Boolean(fieldErrors.telephoneNumber)}
                                            />
                                            &nbsp;&nbsp;
                                            <span style={{ ...FONT, color: 'blue', verticalAlign: 'top' }}>Extension </span>
                                            &nbsp;
                                            <input
                                                type="text"
                                                maxLength={20}
                                                style={{ ...PHONE_INPUT, width: '75px' }}
                                                value={get('telephoneExtension')}
                                                onChange={e => handleChange('telephoneExtension', e.target.value)}
                                                autoComplete="off"
                                                aria-label="Telephone Extension"
                                            />
                                            {errorFor('telephoneNumber')}
                                        </td>
                                    </tr>

                                    {/* Email Address */}
                                    <tr>
                                        <td style={LABEL_TD}>Email Address</td>
                                        <td style={REQUIRED_TD}>*</td>
                                        <td style={INPUT_TD}>
                                            <input
                                                type="email"
                                                maxLength={100}
                                                style={withError(INPUT_STYLE, 'emailAddress')}
                                                value={get('emailAddress')}
                                                onChange={e => handleChange('emailAddress', e.target.value)}
                                                autoComplete="off"
                                                aria-label="Email Address"
                                                aria-invalid={Boolean(fieldErrors.emailAddress)}
                                            />
                                            {errorFor('emailAddress')}
                                        </td>
                                    </tr>

                                    {/* Spacer */}
                                    <tr><td colSpan={3} style={{ paddingTop: '8px' }} /></tr>

                                    {/* Alternate Contact Information header */}
                                    <tr>
                                        <td colSpan={3} className={styles.alternateContactInfoHeader}>
                                            Alternate Contact Information
                                        </td>
                                    </tr>

                                    {/* Alternate Contact */}
                                    <tr>
                                        <td style={LABEL_TD}>Alternate Contact</td>
                                        <td style={{ ...REQUIRED_TD, visibility: 'hidden' }}>*</td>
                                        <td style={INPUT_TD}>
                                            <input
                                                type="text"
                                                maxLength={100}
                                                style={INPUT_STYLE}
                                                value={get('alternateContact')}
                                                onChange={e => handleChange('alternateContact', e.target.value)}
                                                autoComplete="off"
                                                aria-label="Alternate Contact"
                                            />
                                        </td>
                                    </tr>

                                    {/* Alternate Telephone Number: Area Code + Mid 3 + Last 4 + Extension */}
                                    <tr>
                                        <td style={LABEL_TD}>Alternate Telephone Number</td>
                                        <td style={{ ...REQUIRED_TD, visibility: 'hidden' }}>*</td>
                                        <td style={INPUT_TD}>
                                            <input
                                                ref={altPhoneAreaRef}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={3}
                                                style={{ ...PHONE_INPUT, width: '35px' }}
                                                value={get('alternateAreaCode')}
                                                onChange={e => handlePhoneInput('alternateAreaCode', e.target.value, 3, altPhoneMid3Ref)}
                                                autoComplete="off"
                                                aria-label="Alternate Area Code"
                                            />
                                            &nbsp;&nbsp;
                                            <input
                                                ref={altPhoneMid3Ref}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={3}
                                                style={{ ...PHONE_INPUT, width: '35px' }}
                                                value={get('alternateMid3')}
                                                onChange={e => handlePhoneInput('alternateMid3', e.target.value, 3, altPhoneLast4Ref)}
                                                autoComplete="off"
                                                aria-label="Alternate Phone Mid 3"
                                            />
                                            &nbsp;&nbsp;
                                            <input
                                                ref={altPhoneLast4Ref}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={4}
                                                style={{ ...PHONE_INPUT, width: '45px' }}
                                                value={get('alternateLast4')}
                                                onChange={e => handlePhoneInput('alternateLast4', e.target.value, 4, null)}
                                                autoComplete="off"
                                                aria-label="Alternate Phone Last 4"
                                            />
                                            &nbsp;&nbsp;
                                            <span style={{ ...FONT, color: 'blue', verticalAlign: 'top' }}>Extension </span>
                                            &nbsp;
                                            <input
                                                type="text"
                                                maxLength={20}
                                                style={{ ...PHONE_INPUT, width: '75px' }}
                                                value={get('alternateExtension')}
                                                onChange={e => handleChange('alternateExtension', e.target.value)}
                                                autoComplete="off"
                                                aria-label="Alternate Telephone Extension"
                                            />
                                        </td>
                                    </tr>

                                    {/* Alternate Email Address */}
                                    <tr>
                                        <td style={LABEL_TD}>Alternate Email Address</td>
                                        <td style={{ ...REQUIRED_TD, visibility: 'hidden' }}>*</td>
                                        <td style={INPUT_TD}>
                                            <input
                                                type="email"
                                                maxLength={100}
                                                style={withError(INPUT_STYLE, 'alternateEmailAddress')}
                                                value={get('alternateEmailAddress')}
                                                onChange={e => handleChange('alternateEmailAddress', e.target.value)}
                                                autoComplete="off"
                                                aria-label="Alternate Email Address"
                                                aria-invalid={Boolean(fieldErrors.alternateEmailAddress)}
                                            />
                                            {errorFor('alternateEmailAddress')}
                                        </td>
                                    </tr>

                                </tbody>
                            </table>

                        </ATMGrid.Column>
                    </ATMGrid.Row>
                )}

            </ATMGrid>
        </ATMSegment>
        </div>
    );
};

export default FumigationCompany;
